import { Check, Download, FileUpload } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  backend,
  displayDateString,
  getMessageForAxiosError,
  IntakeError,
  UpsertCaseResult,
  UpsertCasesResponse,
} from "Backend";
import PSelect from "components/PSelect";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCasePropertySchemas, selectClientLens, selectClientLensLitigationName } from "redux/slices/clientLens";
import { selectEnableFirmFeatureSet } from "redux/slices/session";
import theme from "theme";
import { P } from "theme/typography";
import triggerDownload from "helpers/triggerDownload";
import LoadingPattern from "components/LoadingPattern";
import axios, { AxiosError } from "axios";
import { FirmWithLenses } from "generated/graphql";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";

const NumberCircle = ({
  number,
  variant,
}: {
  number: number;
  variant: "success" | "error" | "default" | undefined;
}) => {
  const color = () => {
    switch (variant) {
      case "success":
        return theme.palette.success.main;
      case "error":
        return theme.palette.error.main;
      default:
        return theme.palette.grey[100];
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: "3px solid",
        borderColor: color,
      }}>
      <P variant="h5light">{number}</P>
    </Box>
  );
};

const CaseImport = () => {
  const requireFirmSelection = useAppSelector(selectEnableFirmFeatureSet);
  const { lensId } = useParamNumbers(["lensId"]);
  const firms = useFirmsByLensId(lensId);
  const litigationName = useAppSelector(selectClientLensLitigationName);
  const clientLens = useAppSelector(selectClientLens);
  const casePropertyKeys = useAppSelector(selectCasePropertySchemas).map((cp) => cp.key);
  const [uploading, setUploading] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState<FirmWithLenses | undefined>();
  const [checkImportsResponse, setCheckImportsResponse] = useState<UpsertCasesResponse | undefined>(undefined);
  const checkSuccesses: UpsertCaseResult[] = checkImportsResponse?.successes ?? [];
  const checkErrors: IntakeError[] = checkImportsResponse?.failures ?? [];
  const successTotal: number = checkImportsResponse?.successTotal ?? checkSuccesses.length;
  const errorTotal: number = checkImportsResponse?.failureTotal ?? checkErrors.length;
  const isSuccess: boolean = successTotal > 0;
  const isError: boolean = errorTotal > 0;
  const overLimitSuccessText = successTotal > checkSuccesses.length ? "(Only the first 500 are shown)" : undefined;
  const overLimitErrorsText = errorTotal > checkErrors.length ? "(Only the first 500 are shown)" : undefined;

  const [file, setFile] = useState<File | undefined>();
  const snackbar = useSnackbar();

  const inputFile = useRef<HTMLInputElement>(null);

  const boxStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "space-between",
    p: 2,
    border: "1px solid",
    borderColor: "grey.100",
    borderRadius: 1,
  };

  const onFileUploadClick = () => {
    // `current` points to the mounted file input element
    if (inputFile.current) inputFile.current.click();
  };

  const onFile = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setCheckImportsResponse(undefined);
    const fileUploaded = evt.target.files?.[0];
    if (!fileUploaded) return;
    checkFile(fileUploaded);
    setFile(fileUploaded);
    // We reset to allow the user to upload the same file again
    if (inputFile.current) inputFile.current.value = "";
  };

  function checkFile(file: File) {
    if (file && litigationName) {
      setUploading(true);
      backend
        .upsertCasesFromFile(file, true, litigationName, selectedFirm ? selectedFirm.externalId : undefined)
        .then((response) => {
          setCheckImportsResponse(response);
          setUploading(false);
        })
        .catch((error: AxiosError | Error) => {
          setUploading(false);
          // We reset to allow the user to upload a new file as the file could not be validated
          resetFile();
          // An illustration of a way to handle errors more cleanly
          // We can factor this out of course, but we cannot simply keep
          // doing error.response.data because that blows up on all
          // network errors
          let msg: string;
          if (axios.isAxiosError(error)) {
            msg = getMessageForAxiosError(error);
          } else {
            msg = "Something went wrong.";
          }
          snackbar.enqueueSnackbar(msg, {
            variant: "error",
          });
        });
    }
  }

  const downloadTemplate = async () => {
    const { data, fileName } = await backend.downloadCaseTemplate(casePropertyKeys, clientLens.id);
    triggerDownload(
      new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName
    );
  };

  function onFileCompleteUploadClick() {
    if (file) {
      setUploading(true);
      if (litigationName) {
        backend
          .upsertCasesFromFile(file, false, litigationName, selectedFirm ? selectedFirm.externalId : undefined)
          .then(() => {
            resetFile();
            setSelectedFirm(undefined);
            setUploading(false);
            snackbar.enqueueSnackbar("Cases successfully imported", { variant: "success" });
          })
          .catch((error) => {
            resetFile();
            if (error.response?.status === 504) {
              snackbar.enqueueSnackbar(
                "We ran out of time for a standard API call but the import will continue in the background.",
                {
                  variant: "info",
                }
              );
            } else {
              snackbar.enqueueSnackbar(
                error.response?.data?.message ? error.response.data.message : "There was a problem with the upload.",
                { variant: "error" }
              );
            }
          });
      }
    }
  }

  function handleFirmChange(evt) {
    const firm = firms.find((firm) => firm.id === evt.target.value);
    resetFile();
    setSelectedFirm(firm);
  }

  function resetFile() {
    setFile(undefined);
    setCheckImportsResponse(undefined);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}>
      <Box
        sx={{
          display: "flex",
          mt: 2,
          gap: 2,
        }}>
        <Box
          sx={{
            ...boxStyle,
          }}>
          <NumberCircle number={1} variant="default" />
          <P
            variant="h6"
            sx={{
              mt: 1,
            }}>
            Download Template
          </P>
          <P>Download the Case Import Excel Template</P>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Link onClick={downloadTemplate}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Download />}
                sx={{
                  mt: 2,
                }}>
                Download Template
              </Button>
            </Link>
          </Box>
        </Box>
        {requireFirmSelection && (
          <Box
            sx={{
              ...boxStyle,
            }}>
            <Box>
              <NumberCircle number={2} variant={selectedFirm ? "success" : "default"} />
              <P
                variant="h6"
                sx={{
                  mt: 1,
                }}>
                Select Firm
              </P>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <FormControl size="small">
                <InputLabel variant="outlined">Select Firm</InputLabel>
                <PSelect
                  disabled={!!file}
                  label="Select Firm"
                  value={selectedFirm ? selectedFirm.id : ""}
                  sx={{
                    width: 200,
                  }}
                  onChange={handleFirmChange}>
                  <MenuItem key="none" value="">
                    No Firm
                  </MenuItem>

                  {firms.map((firm) => (
                    <MenuItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </MenuItem>
                  ))}
                </PSelect>
              </FormControl>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            ...boxStyle,
          }}>
          <Box>
            <NumberCircle
              number={requireFirmSelection ? 3 : 2}
              variant={isError ? "error" : isSuccess ? "success" : "default"}
            />
            <P
              variant="h6"
              sx={{
                mt: 1,
              }}>
              Import cases
            </P>
            <P>{file ? file.name : "No file selected"}</P>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            {!file ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileUpload />}
                onClick={onFileUploadClick}
                disabled={uploading}
                sx={{
                  mt: 2,
                }}>
                Select Case Import File
                <input
                  ref={inputFile}
                  type="file"
                  autoComplete="off"
                  tabIndex={-1}
                  accept="text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: "none" }}
                  onChange={onFile}
                />
              </Button>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                }}>
                <Button onClick={resetFile}>{isError ? "Try again" : "Cancel"}</Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Check />}
                  onClick={onFileCompleteUploadClick}
                  disabled={uploading || isError}>
                  Complete Upload
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {uploading && <LoadingPattern centered sx={{ flexGrow: 1, mt: 4 }} />}
      {/* Tables */}
      <Box>
        {isSuccess && (
          <Box
            sx={{
              mt: 2,
            }}>
            <P variant="h4">Staged Cases for {clientLens.name}</P>
            <P>{successTotal} cases are ready for import.</P>
            <P>
              {checkImportsResponse?.successCreateCount} Creates, {checkImportsResponse?.successUpdateCount} Updates.{" "}
              {overLimitSuccessText}
            </P>
            <Card
              sx={{
                mt: 2,
              }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Case ID</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Date of Birth</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkSuccesses.map((stagedImport) => (
                    <TableRow key={stagedImport.clientCaseId}>
                      <TableCell>{stagedImport.clientCaseId}</TableCell>
                      <TableCell>{stagedImport.givenName}</TableCell>
                      <TableCell>{stagedImport.surname}</TableCell>
                      <TableCell>{displayDateString(stagedImport.dateOfBirth)}</TableCell>
                      <TableCell>
                        {stagedImport.caseId === -1 ? (
                          <Chip
                            label="New"
                            color="success"
                            sx={{
                              color: "success.contrastText",
                            }}
                          />
                        ) : (
                          <Chip label="Update" color="secondary" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </Box>
        )}

        {isError && (
          <Box
            sx={{
              mt: 2,
            }}>
            <P variant="h4">Issues Found</P>
            <P sx={{ mt: 1 }}>
              The following {errorTotal} cases have issues preventing import. Please correct the issues and try again.{" "}
              {overLimitErrorsText}
            </P>
            <Card
              sx={{
                mt: 2,
              }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Case ID</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Date of Birth</TableCell>
                    <TableCell>Error Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkErrors.map((stagedImport, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{stagedImport.rawRecord?.case_id}</TableCell>
                      <TableCell>{stagedImport.rawRecord?.claimant_given_name}</TableCell>
                      <TableCell>{stagedImport.rawRecord?.claimant_surname}</TableCell>
                      <TableCell>{displayDateString(stagedImport.rawRecord?.claimant_dob)}</TableCell>
                      <TableCell
                        sx={{
                          color: "red",
                        }}>
                        {stagedImport.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CaseImport;
